import { Table, Select, DatePicker, Input, Button, Space, message, Modal, InputNumber, Tag, Spin, Tooltip, Tabs, Popover, Typography, Drawer, Badge, Checkbox } from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone, FilterOutlined, SettingOutlined, ArrowUpOutlined, ArrowDownOutlined, InboxOutlined } from '@ant-design/icons';
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useOrders } from '../hooks/useOrders';
import { useElementHeight } from '../hooks/useElementHeight';
import { useDebounced } from '../hooks/useDebounced';
import { useStores } from '../hooks/useStores';
import { useAuth } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';
import { syncOrders, validateUniuniOrders, updateOrderPackageData, startShipmentBatch, getShipmentBatch, retryShipmentBatch, markOrdersDeleted, restoreOrders } from '../api/orders.api';
import { fetchPickupAddresses } from '../api/pickup_address.api';
import { fetchPickupPrefs, setDefaultPickup, type PickupPref } from '../api/pickup_prefs.api';
import { useQueryClient } from '@tanstack/react-query';

export default function OrdersReport() {
  const { user, isAdmin } = useAuth();
  const { region } = useRegion();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [toolbarRef, toolbarHeight] = useElementHeight<HTMLDivElement>();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounced(searchInput, 300);

  // Cursor pagination state — used when the result set is too large for
  // OFFSET (see MAX_OFFSET on the backend). `cursorStack` remembers the
  // cursor BEFORE each page so Prev can pop back.
  const OFFSET_CAP = 2000;
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  useEffect(() => {
    // Toolbar search feeds directly into filters.searchQuery. Clearing the
    // box removes the filter entirely. Also clears any cursor state so we
    // start at page 1 of the new result set.
    setFilters((prev: any) => {
      const { cursor: _c, cursorDir: _cd, ...rest } = prev;
      const next = { ...rest, page: 1 };
      if (debouncedSearch.trim()) next.searchQuery = debouncedSearch.trim();
      else delete next.searchQuery;
      return next;
    });
    setCursorStack([]);
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps
  const [syncing, setSyncing] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [shipModalOpen, setShipModalOpen] = useState(false);
  const [markDeletedOpen, setMarkDeletedOpen] = useState(false);
  const [markDeletedReason, setMarkDeletedReason] = useState('');
  const [markDeletedBusy, setMarkDeletedBusy] = useState(false);
  const [pkgErrors, setPkgErrors] = useState<Record<string, string[]>>({});
  const [pkgData, setPkgData] = useState<Record<string, any>>({});
  const [pickupAddresses, setPickupAddresses] = useState<any[]>([]);
  const [selectedPickupId, setSelectedPickupId] = useState<number | undefined>(undefined);
  const [pickupPrefs, setPickupPrefs] = useState<PickupPref[]>([]);
  const [validationResults, setValidationResults] = useState<Record<string, any>>({});
  const [validating, setValidating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Batch job state — set after POST /uniuni/shipments/batch returns.
  // Frontend polls GET /uniuni/shipments/batch/:jobId every 2s while
  // status ∈ {pending, processing}. Modal stays open so operator sees progress.
  // Persisted to localStorage per region so a page refresh (or navigation)
  // doesn't lose track of an in-flight batch — banner reappears on remount.
  const batchStorageKey = `activeBatchJobId:${user?.defaultRegion ?? 'default'}`;
  const [batchJobId, setBatchJobIdState] = useState<string | null>(() => {
    try { return localStorage.getItem(batchStorageKey); } catch { return null; }
  });
  const setBatchJobId = (id: string | null): void => {
    setBatchJobIdState(id);
    try {
      if (id) localStorage.setItem(batchStorageKey, id);
      else localStorage.removeItem(batchStorageKey);
    } catch { /* ignore */ }
  };
  const [batchStatus, setBatchStatus] = useState<any>(null);
  const [batchViewerOpen, setBatchViewerOpen] = useState(false);

  // Load pickup addresses on mount so we can (a) show them in the Ship
  // From filter dropdown, and (b) auto-apply a default ship-from filter
  // to the awaiting_shipment tab (only orders shipping from configured
  // pickup warehouses show unless the operator clears the filter).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const addrs = await fetchPickupAddresses();
        if (cancelled) return;
        setPickupAddresses(addrs);
        // Default filter = pickups sharing the SAME physical warehouse
        // as SS's is_default pickup. Rationale:
        //   - SS returns country='US' for every pickup (even the CA
        //     locations), so country can't distinguish physical location.
        //   - inventory_warehouse_id IS authoritative (se-7162 = CA,
        //     se-176533 = FL, etc). All pickups sharing a warehouse_id
        //     ship from the same physical stock and are equivalent for
        //     the "which physical location am I working from" question.
        //   - Anchoring on is_default gives us the operator's normal
        //     workspace by default; adding another warehouse's pickups
        //     is a one-drawer-click override.
        const nonEmpty = (v: unknown): boolean => v != null && String(v).trim() !== '';
        const configured = addrs.filter((a: any) =>
          nonEmpty(a.ss_warehouse_id)
          && nonEmpty(a.customer_no)
          && nonEmpty(a.inventory_warehouse_id)
          && nonEmpty(a.uniuni_warehouse_id));
        const defaultPickup =
          configured.find((a: any) => a.is_default) ?? configured[0];
        const defaultWarehouseId = defaultPickup?.inventory_warehouse_id ?? null;
        const defaultWhIds = defaultWarehouseId
          ? configured
              .filter((a: any) => a.inventory_warehouse_id === defaultWarehouseId)
              .map((a: any) => a.ss_warehouse_id)
              .join(',')
          : '';
        // region is watched via the effect dep chain (region-switch causes
        // AppLayout to remount the Outlet, re-running this effect). No
        // extra region gating needed at the filter step.
        void region;
        if (defaultWhIds) {
          setFilters((prev: any) => (prev.warehouseId != null ? prev : { ...prev, warehouseId: defaultWhIds }));
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [activeTab, setActiveTab] = useState<'awaiting_shipment' | 'other'>('awaiting_shipment');
  // Field names must match the backend Zod enum (lowercase, real column names).
  // Legacy sent `CreateDate` in ShipStation's casing; the new backend rejects that.
  // assignedStores is a number[] on the User model. An EMPTY array means
  // "access to all stores in the user's region(s)" — matches the backend
  // semantic (no storeId filter). Only send storeIds when we have at
  // least one to filter by; otherwise omit and let the backend see all.
  const baseFilters = {
    page: 1,
    pageSize: 20,
    sortBy: 'createDate',
    sortDir: 'DESC',
    ...(user?.assignedStores && user.assignedStores.length > 0
      ? { storeIds: user.assignedStores.join(',') }
      : {}),
  };
  const [filters, setFilters] = useState<any>(baseFilters);

  // Group By — client-side nested aggregation over the current query.
  // Selection order = nesting order (first key = outermost group).
  // Empty array = no grouping (normal paginated table).
  type GroupByKey = 'sku' | 'quantity' | 'store' | 'recipient' | 'orderDate' | 'age';
  const GROUP_OPTIONS: Array<{ value: GroupByKey; label: string }> = [
    { value: 'sku',        label: 'Item SKU' },
    { value: 'quantity',   label: 'Total Quantity' },
    { value: 'store',      label: 'Store' },
    { value: 'recipient',  label: 'Recipient' },
    { value: 'orderDate',  label: 'Order Date' },
    { value: 'age',        label: 'Age' },
  ];
  const groupByPrefsKey = `fninja:orders:groupByKeys:${user?.id ?? 'anon'}`;
  const [groupByKeys, setGroupByKeys] = useState<GroupByKey[]>(() => {
    try {
      const saved = localStorage.getItem(groupByPrefsKey);
      if (saved) return JSON.parse(saved) as GroupByKey[];
    } catch { /* ignore */ }
    return [];
  });
  const setGroupByKeysPersisted = (v: GroupByKey[]) => {
    setGroupByKeys(v);
    try { localStorage.setItem(groupByPrefsKey, JSON.stringify(v)); } catch { /* quota */ }
  };
  const GROUP_FETCH_CAP = 2000;

  const grouping = groupByKeys.length > 0;
  const effectiveFilters = useMemo(() => {
    const groupOverride = grouping ? { page: 1, pageSize: GROUP_FETCH_CAP } : {};
    if (activeTab === 'awaiting_shipment') {
      const { excludeOrderStatus: _, ...rest } = filters;
      return { ...rest, ...groupOverride, orderStatus: 'awaiting_shipment' };
    }
    if (filters.orderStatus && filters.orderStatus !== 'awaiting_shipment') {
      return { ...filters, ...groupOverride };
    }
    const { orderStatus: _, ...rest } = filters;
    return { ...rest, ...groupOverride, excludeOrderStatus: 'awaiting_shipment' };
  }, [filters, activeTab, grouping]);

  // isFetching covers background refetches (tab switch, filter change) that
  // isLoading alone misses — otherwise old data stays visible with no spinner
  // and the user thinks the tab is empty.
  const { data, isLoading, isFetching } = useOrders(effectiveFilters) as { data: any; isLoading: boolean; isFetching: boolean };

  // Stock snapshots for the visible page. Fetched once per data batch so
  // OrdersReport can render a per-row stock chip without N+1 requests.
  type WhSnap = { warehouse_id: string; warehouse_name: string | null; location_id: string | null; location_name: string | null; on_hand: number; available: number | null; snapshot_at: string };
  const [stockBySku, setStockBySku] = useState<Record<string, WhSnap[]>>({});
  const [masterFor, setMasterFor] = useState<Record<string, string>>({});
  const [stockLoading, setStockLoading] = useState(false);
  const [stockDetail, setStockDetail] = useState<any | null>(null); // holds the order for modal
  const [stockDetailLocation, setStockDetailLocation] = useState<string | 'ALL'>('ALL');
  useEffect(() => {
    // Stock lookup only matters for the Awaiting Shipment tab (do we have
    // enough to ship?). Skip on the "other" tab so we don't hit SS for
    // orders that are already shipped/cancelled/etc.
    if (activeTab !== 'awaiting_shipment') { setStockBySku({}); setMasterFor({}); return; }
    const orders: any[] = data?.orders ?? [];
    if (orders.length === 0) { setStockBySku({}); setMasterFor({}); return; }
    const parseItems = (v: any): any[] => (Array.isArray(v) ? v : typeof v === 'string' ? (() => { try { return JSON.parse(v); } catch { return []; } })() : []);
    const seen = new Set<string>();
    const payloadItems: Array<{ sku: string; storeId: number }> = [];
    for (const o of orders) {
      for (const it of parseItems(o.items)) {
        const sku = (it.sku ?? '').toString().trim();
        if (!sku) continue;
        const storeId = Number(o.storeId ?? 0);
        const key = `${sku}::${storeId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        payloadItems.push({ sku, storeId });
      }
    }
    if (payloadItems.length === 0) { setStockBySku({}); setMasterFor({}); return; }
    setStockLoading(true);
    (async () => {
      try {
        const { data: resp } = await (await import('../api/client')).default.post('/shipstation-inventory/inventory/latest-snapshots', { items: payloadItems });
        setStockBySku(resp?.snapshots ?? {});
        setMasterFor(resp?.masterFor ?? {});
      } catch { /* non-critical */ }
      finally { setStockLoading(false); }
    })();
  }, [data?.orders, activeTab]);

  const selectedOrders = (data?.orders ?? []).filter((o: any) => selectedRowKeys.some(k => String(k) === String(o.orderId)));

  function openMarkDeleted() {
    if (selectedOrders.length === 0) return;
    setMarkDeletedReason('');
    setMarkDeletedOpen(true);
  }

  async function confirmMarkDeleted() {
    const orderNumbers = selectedOrders.map((o: any) => o.orderNumber).filter(Boolean);
    const reason = markDeletedReason.trim();
    if (orderNumbers.length === 0 || !reason) return;
    setMarkDeletedBusy(true);
    try {
      const res = await markOrdersDeleted(orderNumbers, reason);
      message.success(`Marked ${res.updated} deleted${res.alreadyDeleted ? ` (${res.alreadyDeleted} already deleted)` : ''}${res.notFound ? ` (${res.notFound} not found)` : ''}`);
      setSelectedRowKeys([]);
      setMarkDeletedOpen(false);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? err?.message ?? 'Mark deleted failed');
    } finally {
      setMarkDeletedBusy(false);
    }
  }

  async function handleRestore() {
    const orderNumbers = selectedOrders.map((o: any) => o.orderNumber).filter(Boolean);
    if (orderNumbers.length === 0) return;
    Modal.confirm({
      title: `Restore ${orderNumbers.length} order${orderNumbers.length === 1 ? '' : 's'}?`,
      content: 'Selected orders will move back to Awaiting Shipment.',
      onOk: async () => {
        try {
          const res = await restoreOrders(orderNumbers);
          message.success(`Restored ${res.updated} order${res.updated === 1 ? '' : 's'}`);
          setSelectedRowKeys([]);
          queryClient.invalidateQueries({ queryKey: ['orders'] });
        } catch (err: any) {
          message.error(err?.response?.data?.message ?? err?.message ?? 'Restore failed');
        }
      },
    });
  }

  const allSelectedAreDeleted = selectedOrders.length > 0 && selectedOrders.every((o: any) => o.orderStatus === 'deleted');

  async function openShipModal() {
    const dimUomMap: Record<string, string> = { inches: 'IN', centimeters: 'CM' };
    const wtUomMap: Record<string, string> = { ounces: 'OZS', pounds: 'LBS', grams: 'KGS', kilograms: 'KGS' };
    const initial: Record<string, any> = {};
    selectedOrders.forEach((o: any) => {
      initial[o.orderId] = {
        length: o.length ?? undefined,
        width: o.width ?? undefined,
        height: o.height ?? undefined,
        dimension_uom: o.dimUOM ? (dimUomMap[o.dimUOM.toLowerCase()] ?? o.dimUOM.toUpperCase()) : undefined,
        weight: o.weight ?? undefined,
        weight_uom: o.weightUOM ? (wtUomMap[o.weightUOM.toLowerCase()] ?? o.weightUOM.toUpperCase()) : undefined,
      };
    });
    setPkgData(initial);
    setPkgErrors({});
    setValidationResults({});
    setSelectedPickupId(undefined);
    try {
      const [addrs, prefs] = await Promise.all([
        fetchPickupAddresses(),
        region ? fetchPickupPrefs(region).catch(() => [] as PickupPref[]) : Promise.resolve([] as PickupPref[]),
      ]);
      setPickupAddresses(addrs);
      setPickupPrefs(prefs);
      // Preselect the user's default pickup for this region if it exists
      // and is still a valid (fully configured) pickup.
      const def = prefs.find((p) => p.is_default);
      if (def) {
        const valid = addrs.find(
          (a: any) =>
            a.id === def.pickup_id &&
            a.customer_no &&
            a.inventory_warehouse_id &&
            a.uniuni_warehouse_id,
        );
        if (valid) setSelectedPickupId(def.pickup_id);
      }
    } catch {
      setPickupAddresses([]);
      setPickupPrefs([]);
    }
    setShipModalOpen(true);
  }

  async function handleSetDefaultPickup() {
    if (!region || !selectedPickupId) return;
    try {
      await setDefaultPickup(region, selectedPickupId);
      // Reflect locally without a round-trip; usage_count stays as-is.
      setPickupPrefs((prev) => {
        const next = prev.map((p) => ({ ...p, is_default: p.pickup_id === selectedPickupId }));
        if (!next.some((p) => p.pickup_id === selectedPickupId)) {
          next.push({ pickup_id: selectedPickupId, is_default: true, usage_count: 0, last_used_at: null });
        }
        return next;
      });
      message.success('Default pickup saved');
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? err?.message ?? 'Could not save default');
    }
  }

  function setPkg(orderId: string, field: string, value: any) {
    setPkgData(prev => ({ ...prev, [orderId]: { ...prev[orderId], [field]: value } }));
    // clear prior validation when pkg data changes
    setValidationResults(prev => {
      if (!prev[orderId]) return prev;
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
  }

  async function handleValidate() {
    if (!selectedPickupId) {
      message.warning('Select a pickup address first.');
      return;
    }
    const required = ['length', 'width', 'height', 'dimension_uom', 'weight', 'weight_uom'];
    const errors: Record<string, string[]> = {};
    selectedOrders.forEach((o: any) => {
      const id = String(o.orderId);
      const pkg = pkgData[id] ?? {};
      const missing = required.filter(f => pkg[f] === undefined || pkg[f] === null || pkg[f] === '');
      if (missing.length) errors[id] = missing;
    });
    setPkgErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setValidating(true);
    try {
      const orders = selectedOrders.map((o: any) => {
        const id = String(o.orderId);
        const pkg = pkgData[id];
        const s = o.shipTo ?? {};
        const countryFull = s.country === 'CA' ? 'Canada' : s.country === 'US' ? 'USA' : s.country;
        // US convention: "City, ST ZIP" — state and zip are space-separated, not
        // comma-separated. UniUni's US geocoder rejects "CITY, ST, ZIP".
        const cityStateZip = [s.city, [s.state, s.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
        const delivery_address = [s.street1, s.street2, cityStateZip, countryFull].filter(Boolean).join(', ');
        return {
          orderId:         o.orderId,
          postal_code:     s.postalCode ?? '',
          city:            s.city ?? '',
          province:        s.state ?? '',
          country:         s.country ?? '',
          delivery_address,
          length:          pkg.length,
          width:           pkg.width,
          height:          pkg.height,
          weight:          pkg.weight,
          weight_uom:      pkg.weight_uom,
          dimension_uom:   pkg.dimension_uom,
        };
      });
      const { results } = await validateUniuniOrders(orders, selectedPickupId);
      setValidationResults(results);
      // Backend persists the results on each order row (Rate + Validation
      // columns), but we don't invalidate here - the modal-close handler
      // does that. Otherwise validate + close = two refetches for the
      // same net effect.
    } catch {
      message.error('Validation request failed.');
    } finally {
      setValidating(false);
    }
  }

  async function handleShipConfirm() {
    const allValid = selectedOrders.every((o: any) => validationResults[String(o.orderId)]?.status === 'SUCCESS');
    if (!allValid) {
      message.warning('Validate all orders successfully before confirming shipment.');
      return;
    }
    if (!selectedPickupId) {
      message.warning('Select a pickup address.');
      return;
    }

    // Build orders payload with package data
    const ordersPayload = selectedOrders.map((o: any) => {
      const id = String(o.orderId);
      const pkg = pkgData[id] ?? {};
      const s = o.shipTo ?? {};
        const countryFull = s.country === 'CA' ? 'Canada' : s.country === 'US' ? 'USA' : s.country;
        // US convention: "City, ST ZIP" — state and zip are space-separated, not
        // comma-separated. UniUni's US geocoder rejects "CITY, ST, ZIP".
        const cityStateZip = [s.city, [s.state, s.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
        const delivery_address = [s.street1, s.street2, cityStateZip, countryFull].filter(Boolean).join(', ');
      return {
        orderId: o.orderId,
        delivery_address,
        length: pkg.length,
        width: pkg.width,
        height: pkg.height,
        dimension_uom: pkg.dimension_uom,
        weight: pkg.weight,
        weight_uom: pkg.weight_uom,
      };
    });

    setConfirming(true);
    try {
      // Save package data first
      try {
        await updateOrderPackageData(ordersPayload);
      } catch {
        message.error('Failed to save package data.');
        return;
      }

      // Start async batch job — returns immediately with { jobId }.
      const { jobId } = await startShipmentBatch(ordersPayload, selectedPickupId);
      setBatchJobId(jobId);
      setBatchStatus({ status: 'pending', total: ordersPayload.length, succeeded: 0, failed: 0, skipped: 0, per_order_state: {} });
      // Polling is handled by the useEffect below.
    } catch (err) {
      const anyErr = err as { message?: string; response?: { data?: { error?: { message?: string } } } };
      const msg = anyErr.response?.data?.error?.message ?? anyErr.message ?? 'Failed to start shipment batch.';
      // eslint-disable-next-line no-console
      console.error('startShipmentBatch failed', err);
      message.error(msg, 8);
    } finally {
      setConfirming(false);
    }
  }

  // Poll the batch job while it's running. Stops when status is terminal.
  useEffect(() => {
    if (!batchJobId) return;
    let cancelled = false;
    const poll = async (): Promise<void> => {
      try {
        const job = await getShipmentBatch(batchJobId);
        if (cancelled) return;
        setBatchStatus(job);
        // Merge per-order results into validationResults so the same "Status"
        // column renders success ticks / red icons + popover reasons.
        const merged: Record<string, unknown> = {};
        for (const [orderId, s] of Object.entries(job.per_order_state)) {
          if (s.status === 'success') {
            merged[orderId] = { status: 'SUCCESS', trackingNumber: s.trackingNumber };
          } else if (s.status === 'error') {
            merged[orderId] = { status: 'ERROR', error: s.error ?? 'Shipment failed' };
          } else if (s.status === 'skipped') {
            merged[orderId] = { status: 'ERROR', error: s.error ?? 'Skipped' };
          }
        }
        if (Object.keys(merged).length > 0) {
          setValidationResults((prev: any) => ({ ...prev, ...merged }));
        }
        if (job.status === 'complete' || job.status === 'failed') {
          queryClient.invalidateQueries({ queryKey: ['orders'] });
          queryClient.invalidateQueries({ queryKey: ['uniuniShipments'] });
          if (job.status === 'failed') {
            message.error(`Batch failed: ${job.error_message ?? 'unknown error'}`, 8);
          } else if (job.failed === 0 && job.skipped === 0) {
            message.success(`${job.succeeded} shipment(s) created.`);
            setShipModalOpen(false);
            setBatchJobId(null);
          } else {
            message.warning(`${job.succeeded} succeeded, ${job.failed} failed, ${job.skipped} skipped. Click red icons for details or Retry Failed.`);
            // Leave the banner up so operator can reopen the batch to see
            // per-order errors even after they close the ship modal.
          }
          return;
        }
        // Still running — poll again.
        setTimeout(() => { if (!cancelled) void poll(); }, 2000);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('batch poll failed', err);
        setTimeout(() => { if (!cancelled) void poll(); }, 5000);
      }
    };
    void poll();
    return () => { cancelled = true; };
  }, [batchJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRetryBatch(): Promise<void> {
    if (!batchJobId) return;
    try {
      const { retried } = await retryShipmentBatch(batchJobId);
      if (retried === 0) {
        message.info('No failed orders to retry.');
        return;
      }
      message.success(`Retrying ${retried} order(s)...`);
      // Trigger a fresh poll by resetting the state; the useEffect above
      // will resume polling because jobId is unchanged but status flipped
      // back to pending on the server.
      const job = await getShipmentBatch(batchJobId);
      setBatchStatus(job);
    } catch (err) {
      const anyErr = err as { message?: string };
      message.error(`Retry failed: ${anyErr.message ?? 'unknown'}`);
    }
  }

  const stores = useStores();
  const storeNameById = new Map<number, string>(
    (stores ?? []).map((s: any) => [Number(s.storeId), s.storeName as string]),
  );

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Derive human-readable chips from the currently APPLIED (not local) filters.
  // Each chip carries an onRemove that clears just that filter and re-applies.
  type Chip = { key: string; label: string; onRemove: () => void };
  const activeChips = useMemo<Chip[]>(() => {
    const chips: Chip[] = [];
    const applied: any = filters;
    const remove = (mutate: (next: any) => void) => () => {
      const next = { ...applied };
      mutate(next);
      setFilters(next);
    };
    if (applied.storeId && !Array.isArray(applied.storeId)) {
      const name = storeNameById.get(Number(applied.storeId)) ?? `#${applied.storeId}`;
      chips.push({ key: 'storeId', label: `Store: ${name}`, onRemove: remove((n) => { delete n.storeId; }) });
    }
    if (applied.orderStatus && applied.orderStatus !== 'awaiting_shipment') {
      chips.push({ key: 'orderStatus', label: `Status: ${applied.orderStatus}`, onRemove: remove((n) => { delete n.orderStatus; }) });
    }
    if (applied.searchQuery) {
      chips.push({ key: 'searchQuery', label: `Search: ${applied.searchQuery}`, onRemove: remove((n) => { delete n.searchQuery; }) });
    }
    if (applied.dateFrom) {
      chips.push({ key: 'dateFrom', label: `From: ${applied.dateFrom}`, onRemove: remove((n) => { delete n.dateFrom; }) });
    }
    if (applied.dateTo) {
      chips.push({ key: 'dateTo', label: `To: ${applied.dateTo}`, onRemove: remove((n) => { delete n.dateTo; }) });
    }
    if (applied.validationStatus) {
      chips.push({ key: 'validationStatus', label: `Validation: ${applied.validationStatus}`, onRemove: remove((n) => { delete n.validationStatus; }) });
    }
    if (applied.rateMin != null || applied.rateMax != null) {
      const lo = applied.rateMin != null ? `$${applied.rateMin}` : '';
      const hi = applied.rateMax != null ? `$${applied.rateMax}` : '';
      chips.push({
        key: 'rate',
        label: `Rate: ${lo || '0'} – ${hi || '∞'}`,
        onRemove: remove((n) => { delete n.rateMin; delete n.rateMax; }),
      });
    }
    if (applied.weightMin != null || applied.weightMax != null) {
      const lo = applied.weightMin != null ? applied.weightMin : '';
      const hi = applied.weightMax != null ? applied.weightMax : '';
      chips.push({
        key: 'weight',
        label: `Weight: ${lo || '0'} - ${hi || 'any'}`,
        onRemove: remove((n) => { delete n.weightMin; delete n.weightMax; }),
      });
    }
    if (applied.itemName) {
      chips.push({ key: 'itemName', label: `Item: ${applied.itemName}`, onRemove: remove((n) => { delete n.itemName; }) });
    }
    if (applied.warehouseId) {
      const ids = String(applied.warehouseId).split(',').map((s) => s.trim()).filter(Boolean);
      const nameFor = (id: string) => {
        const a = pickupAddresses.find((p: any) => String(p.ss_warehouse_id) === id);
        return a?.name ?? id;
      };
      // Long lists blow past the toolbar width and force horizontal scroll
      // because AntD Tag is white-space:nowrap. Collapse anything over 3 to
      // "N warehouses" — full list is still one click away in the drawer.
      const label = ids.length > 3
        ? `${ids.length} warehouses`
        : ids.map(nameFor).join(', ');
      chips.push({ key: 'warehouseId', label: `Ship From: ${label}`, onRemove: remove((n) => { delete n.warehouseId; }) });
    }
    return chips;
  }, [filters, storeNameById]);

  // ─── Column customization ─────────────────────────────────────────────
  // Each column has a stable `id` used for the user preference. Reordering
  // and show/hide are driven by the persisted `colPrefs`.
  // TODO: sync colPrefs to a backend endpoint (e.g. /users/me/preferences)
  //       so switching device/browser retains the setup. localStorage is Phase 1.
  const columnCatalog = useMemo(() => ([
    {
      id: 'orderId',
      title: 'Order ID',
      col: {
        title: 'Order ID', dataIndex: 'orderId',
        render: (id: number | string, r: any) => (
          <a onClick={() => navigate(`/order/${r.orderId}`)} style={{ cursor: 'pointer' }}>{id}</a>
        ),
      },
    },
    { id: 'orderStatus', title: 'Status', col: { title: 'Status', dataIndex: 'orderStatus', sorter: true } },
    {
      id: 'store', title: 'Store',
      col: {
        title: 'Store', dataIndex: 'storeId',
        render: (id: number) => storeNameById.get(Number(id)) ?? (id ? `#${id}` : '-'),
      },
    },
    {
      id: 'orderDate', title: 'Order Date',
      col: {
        title: 'Order Date', dataIndex: 'orderDate', sorter: true,
        render: (date: string) => date ? new Date(date).toLocaleDateString('en-US') : '-',
      },
    },
    {
      id: 'orderNumber', title: 'Order Number',
      col: {
        title: 'Order Number', dataIndex: 'orderNumber', sorter: true,
        render: (num: string, r: any) => (
          <a onClick={() => navigate(`/order/${r.orderId}`)} style={{ cursor: 'pointer' }}>{num}</a>
        ),
      },
    },
    {
      id: 'shipFrom', title: 'Ship From',
      col: {
        title: 'Ship From', key: 'shipFrom',
        render: (_: any, r: any) => {
          // advancedOptions.warehouseId is SS's numeric V1 warehouseId.
          // Map to configured pickup name; fall back to the raw id.
          const wid = r.advancedOptions?.warehouseId;
          if (wid == null || wid === '') return '-';
          const pick = pickupAddresses.find((p: any) => String(p.ss_warehouse_id) === String(wid));
          return pick?.name ?? `#${wid}`;
        },
      },
    },
    { id: 'customerName', title: 'Customer Name', col: { title: 'Customer Name', dataIndex: ['shipTo', 'name'], key: 'customerName', sorter: true } },
    {
      id: 'customerContact', title: 'Contact',
      col: {
        title: 'Contact', key: 'customerContact',
        render: (_: any, r: any) => {
          // Prefer phone (what warehouse/carrier actually needs), fall back
          // to email. MySQL renders JSON null as string "null" via
          // JSON_UNQUOTE, so filter that + other bogus values defensively.
          const raw = r.shipTo?.phone;
          const cleaned = typeof raw === 'string' ? raw.trim() : '';
          const isPlaceholder = cleaned === '' || cleaned.toLowerCase() === 'null' || /^\+?1?0+$/.test(cleaned);
          if (!isPlaceholder) return cleaned;
          return r.customerEmail ?? '-';
        },
      },
    },
    { id: 'city', title: 'City', col: { title: 'City', dataIndex: ['shipTo', 'city'], key: 'city', sorter: true } },
    { id: 'state', title: 'State', col: { title: 'State', dataIndex: ['shipTo', 'state'], key: 'state', sorter: true } },
    { id: 'postalCode', title: 'Postal Code', col: { title: 'Postal Code', dataIndex: ['shipTo', 'postalCode'], key: 'postalCode', sorter: true } },
    { id: 'country', title: 'Country', col: { title: 'Country', dataIndex: ['shipTo', 'country'], key: 'country', sorter: true } },
    {
      id: 'weight', title: 'Weight',
      col: {
        title: 'Weight', dataIndex: 'weight',
        render: (w: number | null, r: any) => (w ? `${w} ${r.weightUOM ?? ''}`.trim() : '-'),
      },
    },
    {
      id: 'rate', title: 'Rate',
      col: {
        title: 'Rate', dataIndex: 'validation_charge',
        render: (v: number | null) => v != null ? `$${Number(v).toFixed(2)}` : '-',
      },
    },
    {
      id: 'validation', title: 'Validation',
      col: {
        title: 'Validation',
        dataIndex: 'validation_status',
        render: (status: string | null, r: any) => {
          if (!status) return <span style={{ color: '#bbb' }}>—</span>;
          const ok = status === 'SUCCESS';
          const icon = ok
            ? <CheckCircleTwoTone twoToneColor="#52c41a" />
            : <CloseCircleTwoTone twoToneColor="#ff4d4f" />;
          const tip = (
            <div style={{ maxWidth: 320 }}>
              <div><strong>Status:</strong> {status}</div>
              {r.validation_msg && <div><strong>Reason:</strong> {r.validation_msg}</div>}
              {r.validation_eta && <div><strong>ETA:</strong> {r.validation_eta}</div>}
              {r.validation_charge != null && <div><strong>Rate:</strong> ${Number(r.validation_charge).toFixed(2)}</div>}
              {r.validated_at && <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>Validated: {new Date(r.validated_at).toLocaleString()}</div>}
            </div>
          );
          return <Tooltip title={tip}>{icon}</Tooltip>;
        },
      },
    },
    {
      id: 'items', title: 'Items',
      col: {
        title: 'Items', dataIndex: 'items',
        render: (items: any) => {
          const list: any[] = Array.isArray(items)
            ? items
            : typeof items === 'string'
              ? (() => { try { return JSON.parse(items); } catch { return []; } })()
              : [];
          if (list.length === 0) return '-';
          const label = list.map((i) => `${i.name ?? i.sku ?? '?'} x${i.quantity ?? 1}`).join(', ');
          return label.length > 80 ? label.slice(0, 77) + '…' : label;
        },
      },
    },
    {
      id: 'stock', title: 'Stock',
      col: {
        title: 'Stock', key: 'stock', width: 180,
        render: (_: any, record: any) => {
          const list: any[] = Array.isArray(record.items)
            ? record.items
            : typeof record.items === 'string'
              ? (() => { try { return JSON.parse(record.items); } catch { return []; } })()
              : [];
          if (list.length === 0) return '-';
          if (stockLoading) return <Spin size="small" />;
          const storeId = Number(record.storeId ?? 0);
          // Aggregate per-warehouse min-available across items in the order.
          // For each warehouse, worst-case available = min(item.available across items).
          type PerLoc = { key: string; label: string; min_avail: number };
          const perLoc: Record<string, PerLoc> = {};
          const tipLines: string[] = [];
          let anyKnown = false;
          for (const it of list) {
            const sku = (it.sku ?? '').toString().trim();
            if (!sku) continue;
            const qty = Number(it.quantity ?? 1);
            const master = masterFor[`${sku}::${storeId}`] ?? sku;
            const whList = stockBySku[master] ?? [];
            if (whList.length === 0) { tipLines.push(`${sku}: no snapshot`); continue; }
            anyKnown = true;
            for (const wh of whList) {
              const avail = wh.available ?? wh.on_hand;
              const locKey = wh.location_id ?? wh.warehouse_id;
              const locLabel = wh.location_name ?? wh.warehouse_name ?? wh.warehouse_id;
              const prev = perLoc[locKey];
              if (!prev || avail < prev.min_avail) {
                perLoc[locKey] = { key: locKey, label: locLabel, min_avail: avail };
              }
              tipLines.push(`${sku}→${master} @ ${locLabel}: ${avail} (need ${qty})`);
            }
          }
          if (!anyKnown) return <Tag>Unknown</Tag>;
          const totalQty = list.reduce((s: number, it: any) => s + Number(it.quantity ?? 1), 0);
          const rows = Object.values(perLoc).sort((a, b) => a.label.localeCompare(b.label));
          const worstAvail = Math.min(...rows.map((r) => r.min_avail));
          const totalAvail = rows.reduce((s, r) => s + Math.max(0, r.min_avail), 0);
          let color: 'green' | 'orange' | 'red' = 'green';
          if (totalAvail <= 0 || totalAvail < totalQty) color = 'red';
          else if (totalAvail < totalQty * 2) color = 'orange';
          const shortLabel = (n: string | null) => (n ? n.replace(/^Warehouse\s+/i, '').split(/[\s-]+/)[0] : '?');
          return (
            <div
              onClick={(e) => { e.stopPropagation(); setStockDetailLocation('ALL'); setStockDetail(record); }}
              style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2, lineHeight: '16px' }}
              title="Click for full breakdown"
            >
              {rows.map((r) => (
                <Tag key={r.key} color={r.min_avail < totalQty ? (r.min_avail <= 0 ? 'red' : 'orange') : 'green'} style={{ margin: 0 }}>
                  {shortLabel(r.label)}: {r.min_avail}
                </Tag>
              ))}
              {rows.length > 1 && (
                <span style={{ fontSize: 11, color: color === 'red' ? '#cf1322' : color === 'orange' ? '#d46b08' : '#389e0d' }}>
                  Σ {totalAvail} / need {totalQty}
                </span>
              )}
            </div>
          );
          void worstAvail;
          void tipLines;
        },
      },
    },
  ]), [navigate, storeNameById, stockBySku, masterFor, stockLoading]);

  const defaultOrder = columnCatalog.map((c) => c.id);
  const prefsKey = `fninja:orders:cols:${user?.id ?? 'anon'}`;
  const [colPrefs, setColPrefs] = useState<{ order: string[]; hidden: string[] }>(() => {
    try {
      const saved = localStorage.getItem(prefsKey);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return { order: defaultOrder, hidden: [] };
  });
  const savePrefs = (next: { order: string[]; hidden: string[] }) => {
    setColPrefs(next);
    try { localStorage.setItem(prefsKey, JSON.stringify(next)); } catch { /* quota */ }
  };
  const resetPrefs = () => savePrefs({ order: defaultOrder, hidden: [] });

  const visibleColumns = useMemo(() => {
    const byId = new Map(columnCatalog.map((c) => [c.id, c]));
    const ordered: string[] = [
      ...colPrefs.order.filter((id) => byId.has(id)),
      ...defaultOrder.filter((id) => !colPrefs.order.includes(id)),
    ];
    const hidden = new Set(colPrefs.hidden);
    // Stock is only meaningful on Awaiting Shipment ("can we fulfill?") —
    // suppress on other tabs regardless of column prefs.
    if (activeTab !== 'awaiting_shipment') hidden.add('stock');
    return ordered.filter((id) => !hidden.has(id)).map((id) => byId.get(id)!.col);
  }, [columnCatalog, colPrefs, activeTab]);

  const [colDrawerOpen, setColDrawerOpen] = useState(false);
  const [invDrawerOpen, setInvDrawerOpen] = useState(false);

  // Group By key extractors. Return string label + optional numeric sort weight.
  const parseItems = (v: any): any[] => (Array.isArray(v) ? v : typeof v === 'string' ? (() => { try { return JSON.parse(v); } catch { return []; } })() : []);
  const shipToOf = (v: any): any => (v && typeof v === 'object' ? v : typeof v === 'string' ? (() => { try { return JSON.parse(v); } catch { return {}; } })() : {});
  const groupKeyOf = (o: any, key: GroupByKey): string => {
    switch (key) {
      case 'sku': {
        const items = parseItems(o.items);
        if (items.length === 0) return '(no items)';
        if (items.length > 1) {
          const uniq = new Set(items.map((i) => i.sku ?? i.name ?? '?'));
          if (uniq.size > 1) return '(multiple)';
        }
        return String(items[0].sku ?? items[0].name ?? '?');
      }
      case 'quantity': {
        const items = parseItems(o.items);
        const total = items.reduce((s, i) => s + Number(i.quantity ?? 1), 0);
        return String(total || 0);
      }
      case 'store': {
        const id = Number(o.storeId);
        return storeNameById.get(id) ?? (Number.isFinite(id) ? `Store ${id}` : '(no store)');
      }
      case 'recipient': {
        const st = shipToOf(o.shipTo);
        return (st?.name ?? o.customerName ?? o.customerEmail ?? '(unknown)').toString();
      }
      case 'orderDate': {
        const d = o.orderDate ? dayjs(o.orderDate).format('YYYY-MM-DD') : '(no date)';
        return d;
      }
      case 'age': {
        if (!o.createDate) return '(no date)';
        const hrs = dayjs().diff(dayjs(o.createDate), 'hour');
        if (hrs < 24) return '< 1 day';
        if (hrs < 48) return '1-2 days';
        if (hrs < 72) return '2-3 days';
        if (hrs < 168) return '3-7 days';
        return '7+ days';
      }
      default: return '';
    }
  };
  const groupLabelOf = (key: GroupByKey): string =>
    GROUP_OPTIONS.find((o) => o.value === key)?.label ?? key;

  interface OrderGroup { key: string; label: string; orders: any[]; count: number; totalQty: number }
  const buildGroups = (orders: any[], key: GroupByKey): OrderGroup[] => {
    const map = new Map<string, OrderGroup>();
    for (const o of orders) {
      const k = groupKeyOf(o, key);
      const items = parseItems(o.items);
      const qty = items.reduce((s, i) => s + Number(i.quantity ?? 1), 0);
      const existing = map.get(k);
      if (existing) {
        existing.orders.push(o);
        existing.count++;
        existing.totalQty += qty;
      } else {
        map.set(k, { key: k, label: k, orders: [o], count: 1, totalQty: qty });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  };

  // Aggregate SKUs across currently-selected orders. Mirrors ShipStation's
  // "Items" sidebar - one row per SKU with total qty needed for the batch,
  // so the picker knows exactly what to grab from the warehouse.
  const inventorySummary = useMemo(() => {
    const byKey = new Map<string, { sku: string; name: string; qty: number; orderCount: number }>();
    for (const o of selectedOrders) {
      const items: any[] = Array.isArray(o.items)
        ? o.items
        : typeof o.items === 'string'
          ? (() => { try { return JSON.parse(o.items); } catch { return []; } })()
          : [];
      const seenSkus = new Set<string>();
      for (const it of items) {
        const sku = (it.sku ?? it.name ?? 'unknown').toString();
        const name = (it.name ?? sku).toString();
        const qty = Number(it.quantity ?? 1);
        const existing = byKey.get(sku);
        if (existing) {
          existing.qty += qty;
          if (!seenSkus.has(sku)) existing.orderCount++;
        } else {
          byKey.set(sku, { sku, name, qty, orderCount: 1 });
        }
        seenSkus.add(sku);
      }
    }
    return Array.from(byKey.values()).sort((a, b) => b.qty - a.qty);
  }, [selectedOrders]);
  const totalItems = inventorySummary.reduce((sum, r) => sum + r.qty, 0);

  async function fetchNewOrders() {
    setSyncing(true);
    try {
      await syncOrders();
      message.success('Sync started — new orders will appear shortly.');
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['orders'] }), 5000);
    } catch {
      message.error('Sync failed. Please try again.');
    } finally {
      setSyncing(false);
    }
  }

  const [local, setLocal] = useState<any>({
    storeId: undefined,
    status: undefined,
    orderNumberOp: 'contains',
    orderNumber: '',
    nameOp: 'contains',
    name: '',
    emailOp: 'contains',
    email: '',
    cityOp: 'contains',
    city: '',
    postalCodeOp: 'contains',
    postalCode: '',
    countryOp: 'contains',
    country: '',
    itemName: '',
    warehouseIds: undefined as string[] | undefined,
    from: undefined,
    to: undefined
  });

  function applyFilters() {
    // Field names must match backend orders list schema (orders.schemas.ts).
    // Supported: orderStatus, storeId, searchQuery, dateFrom, dateTo, sortBy,
    // sortDir. The multi-field text filters (orderNumber/name/email/city/etc)
    // are bundled into `searchQuery`, which the repository queries across
    // orderNumber, customerEmail, and shipTo.name. city/postalCode/country
    // aren't queried yet — they'd need SQL support to activate.
    const mapped: any = { ...baseFilters, page: 1 };
    if (local.storeId) {
      mapped.storeId = local.storeId;
    } else if (user?.assignedStores && user.assignedStores.length > 0) {
      mapped.storeId = user.assignedStores;
    }
    if (local.status) mapped.orderStatus = local.status;
    if (local.from)   mapped.dateFrom    = local.from;
    if (local.to)     mapped.dateTo      = local.to;
    if (local.validationStatus) mapped.validationStatus = local.validationStatus;
    if (local.rateMin   != null && local.rateMin   !== '') mapped.rateMin   = local.rateMin;
    if (local.rateMax   != null && local.rateMax   !== '') mapped.rateMax   = local.rateMax;
    if (local.weightMin != null && local.weightMin !== '') mapped.weightMin = local.weightMin;
    if (local.weightMax != null && local.weightMax !== '') mapped.weightMax = local.weightMax;

    const searchParts = [local.orderNumber, local.name, local.email]
      .map((v) => (v ?? '').trim())
      .filter(Boolean);
    if (searchParts.length > 0) mapped.searchQuery = searchParts.join(' ');
    if ((local.itemName ?? '').trim()) mapped.itemName = local.itemName.trim();
    if (Array.isArray(local.warehouseIds) && local.warehouseIds.length > 0) {
      mapped.warehouseId = local.warehouseIds.join(',');
    }

    setFilters(mapped);
  }

  function clearFilters() {
    setLocal({
      storeId: undefined, status: undefined,
      orderNumberOp: 'contains', orderNumber: '',
      nameOp: 'contains', name: '',
      emailOp: 'contains', email: '',
      cityOp: 'contains', city: '',
      postalCodeOp: 'contains', postalCode: '',
      countryOp: 'contains', country: '',
      itemName: '',
      warehouseIds: undefined,
      from: undefined, to: undefined
    });
    setSearchInput('');
    setFilters(baseFilters);
  }

  const allValidated = selectedOrders.length > 0 && selectedOrders.every((o: any) => validationResults[String(o.orderId)]?.status === 'SUCCESS');

  // Innermost orders table shared by both plain + grouped views. Multi-item
  // orders are collapsed by default with a "(N items)" summary; expanding a
  // row shows each item as a compact sub-row.
  // Cursor navigation. Push current cursor to the stack so Prev can walk
  // back one step at a time; Next uses data.nextCursor from the last query.
  const goNext = () => {
    if (!data?.nextCursor) return;
    setCursorStack((s) => [...s, filters.cursor ?? '']);
    setFilters((f: any) => ({ ...f, cursor: data.nextCursor, cursorDir: 'next' }));
  };
  const goPrev = () => {
    setCursorStack((s) => {
      const next = [...s];
      const prior = next.pop() ?? '';
      setFilters((f: any) => {
        const { cursor: _c, cursorDir: _cd, ...rest } = f;
        return prior ? { ...rest, cursor: prior, cursorDir: 'next' } : rest;
      });
      return next;
    });
  };
  const goFirst = () => {
    setCursorStack([]);
    setFilters((f: any) => {
      const { cursor: _c, cursorDir: _cd, ...rest } = f;
      return { ...rest, page: 1 };
    });
  };

  const renderOrdersTable = (orders: any[], nested: boolean) => {
    const useCursor = !nested && (data?.total ?? 0) > OFFSET_CAP;
    return (
    <Table
      sticky={nested ? false : { offsetHeader: toolbarHeight }}
      size={nested ? 'small' : 'middle'}
      rowKey="orderId"
      dataSource={orders}
      columns={visibleColumns}
      pagination={nested || useCursor ? false : {
        current: filters.page,
        pageSize: filters.pageSize,
        total: data?.total,
        // Cap the page selector so users can't hit the backend OFFSET cap.
        showTotal: (t) => `${t} orders`,
        pageSizeOptions: [20, 50, 100, 250, 500],
      }}
      rowSelection={{
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
      }}
      onChange={nested ? undefined : (pagination: any, _f: any, sorter: any) => {
        const newFilters: any = { ...filters, page: pagination.current, pageSize: pagination.pageSize };
        if (sorter.field) {
          const ALLOWED_SORTS = new Set(['orderDate','orderNumber','orderStatus','orderTotal','createDate','modifyDate','shipDate']);
          newFilters.sortBy = ALLOWED_SORTS.has(sorter.field) ? sorter.field : 'createDate';
          newFilters.sortDir = sorter.order === 'ascend' ? 'ASC' : 'DESC';
        }
        // Sort change resets cursor navigation.
        delete newFilters.cursor;
        delete newFilters.cursorDir;
        setCursorStack([]);
        setFilters(newFilters);
      }}
      footer={useCursor ? () => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#666', fontSize: 12 }}>
            {(data?.total ?? 0).toLocaleString()} orders — showing {orders.length} on this page. Narrow filters or use search to jump directly.
          </span>
          <Space>
            <Button size="small" onClick={goFirst} disabled={cursorStack.length === 0 && !filters.cursor}>« First</Button>
            <Button size="small" onClick={goPrev} disabled={cursorStack.length === 0}>‹ Prev</Button>
            <span style={{ color: '#666', fontSize: 12 }}>
              Page {cursorStack.length + 1}
            </span>
            <Button size="small" onClick={goNext} disabled={!data?.nextCursor}>Next ›</Button>
          </Space>
        </div>
      ) : undefined}
      loading={!nested ? { spinning: isLoading || isFetching, tip: 'Loading...' } : undefined}
      expandable={{
        rowExpandable: (o: any) => parseItems(o.items).length > 1,
        expandedRowRender: (o: any) => {
          const items = parseItems(o.items);
          // Constrain width so Qty sits right next to Item Name — full-width
          // stretches Qty to the far right which makes it hard to associate
          // qty with the line.
          return (
            <div style={{ maxWidth: 720 }}>
              <Table
                size="small"
                tableLayout="auto"
                rowKey={(it: any, idx?: number) => `${o.orderId}-${it.orderItemId ?? it.sku ?? idx}`}
                dataSource={items}
                pagination={false}
                showHeader
                columns={[
                  { title: 'SKU', dataIndex: 'sku', key: 'sku', width: 180,
                    render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v}</span> },
                  { title: 'Item Name', dataIndex: 'name', key: 'name',
                    render: (v: string) => <span>{v}</span> },
                  { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 60, align: 'right' as const },
                ]}
              />
            </div>
          );
        },
      }}
    />
    );
  };

  const renderGroupedLevel = (orders: any[], depth: number): React.ReactNode => {
    if (depth >= groupByKeys.length) return renderOrdersTable(orders, true);
    const key = groupByKeys[depth];
    const groups = buildGroups(orders, key);
    return (
      <Table
        sticky={depth === 0 ? { offsetHeader: toolbarHeight } : false}
        size="small"
        rowKey="key"
        dataSource={groups}
        pagination={depth === 0 ? { pageSize: 50, showSizeChanger: true, hideOnSinglePage: true } : false}
        columns={[
          { title: groupLabelOf(key), dataIndex: 'label', key: 'label',
            render: (v: string, g: OrderGroup) => (
              <span>
                <strong>{groupLabelOf(key)}:</strong> {v}
                <Tag style={{ marginLeft: 8 }}>{g.count} orders</Tag>
              </span>
            ),
          },
          { title: 'Orders', dataIndex: 'count', key: 'count', width: 90 },
          { title: 'Total Qty', dataIndex: 'totalQty', key: 'totalQty', width: 110 },
          { title: 'Select group', key: 'select', width: 130,
            render: (_: unknown, g: OrderGroup) => {
              const ids = g.orders.map((o) => o.orderId);
              const allSelected = ids.every((id) => selectedRowKeys.includes(id));
              return (
                <Button size="small" onClick={() => {
                  const set = new Set(selectedRowKeys);
                  if (allSelected) ids.forEach((id) => set.delete(id));
                  else ids.forEach((id) => set.add(id));
                  setSelectedRowKeys(Array.from(set));
                }}>{allSelected ? 'Deselect' : 'Select all'}</Button>
              );
            },
          },
        ]}
        expandable={{
          expandedRowRender: (g: OrderGroup) => renderGroupedLevel(g.orders, depth + 1),
        }}
      />
    );
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Sticky toolbar: tabs + action row + active-filter chips stay pinned
          to the top of the viewport while the table body scrolls. */}
      <div ref={toolbarRef} style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        background: '#fff',
        padding: '8px 16px',
        boxShadow: '0 2px 4px -2px rgba(0,0,0,0.08)',
      }}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key as 'awaiting_shipment' | 'other');
            setFilters({ ...baseFilters, page: 1 });
            setLocal((prev: any) => ({ ...prev, status: undefined }));
            setSelectedRowKeys([]);
          }}
          items={[
            { key: 'awaiting_shipment', label: 'Awaiting Shipment' },
            { key: 'other', label: 'Other Orders' },
          ]}
          style={{ marginBottom: 0 }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <Space>
            <Button type="primary" loading={syncing} onClick={fetchNewOrders}>Fetch New Orders</Button>
            <Button
              type="default"
              disabled={selectedRowKeys.length === 0}
              onClick={openShipModal}
            >
              Ship Selected {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}
            </Button>
            {isAdmin && (
              allSelectedAreDeleted ? (
                <Button
                  disabled={selectedRowKeys.length === 0}
                  onClick={handleRestore}
                >
                  Restore {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}
                </Button>
              ) : (
                <Button
                  danger
                  disabled={selectedRowKeys.length === 0}
                  onClick={openMarkDeleted}
                >
                  Mark Deleted {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}
                </Button>
              )
            )}
            <Input.Search
              placeholder="Search order #, tracking, SKU, batch, or customer name…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={(v) => setSearchInput(v)}
              allowClear
              style={{ width: 320 }}
            />
            <Badge count={activeChips.length} size="small" offset={[-4, 4]}>
              <Button icon={<FilterOutlined />} onClick={() => {
                // Sync applied filters into the drawer's local state so the
                // "Ship From" multiselect (and others) reflect what's active.
                setLocal((prev: any) => ({
                  ...prev,
                  warehouseIds: filters.warehouseId
                    ? String(filters.warehouseId).split(',').map((s: string) => s.trim()).filter(Boolean)
                    : undefined,
                }));
                setFilterDrawerOpen(true);
              }}>
                Filters
              </Button>
            </Badge>
            <Button icon={<SettingOutlined />} onClick={() => setColDrawerOpen(true)}>
              Columns
            </Button>
            <Space.Compact>
              <Button style={{ cursor: 'default', pointerEvents: 'none' }}>Group By</Button>
              <Select
                mode="multiple"
                allowClear
                style={{ minWidth: 260 }}
                placeholder="None"
                value={groupByKeys}
                onChange={(v) => setGroupByKeysPersisted(v as GroupByKey[])}
                options={GROUP_OPTIONS}
                maxTagCount="responsive"
              />
            </Space.Compact>
            <Badge count={selectedRowKeys.length} size="small" offset={[-4, 4]}>
              <Button
                icon={<InboxOutlined />}
                disabled={selectedRowKeys.length === 0}
                onClick={() => setInvDrawerOpen(true)}
              >
                Pick Summary
              </Button>
            </Badge>
          </Space>
          <span><strong>Total Record:</strong> {(data as any)?.total || 0}</span>
        </div>
        {activeChips.length > 0 && (
          <div style={{ marginBottom: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', minWidth: 0 }}>
            <span style={{ color: '#666', fontSize: 12 }}>Active filters:</span>
            {activeChips.map((c) => (
              <Tag
                key={c.key}
                closable
                onClose={c.onRemove}
                // Cap tag width + truncate — belt-and-suspenders alongside
                // the label-shortening logic in activeChips. Prevents any
                // single long-string filter from causing horizontal scroll.
                style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={c.label}
              >
                {c.label}
              </Tag>
            ))}
            <a onClick={clearFilters} style={{ fontSize: 12 }}>Clear all</a>
          </div>
        )}
      </div>
      <div>
        {grouping && data?.total > GROUP_FETCH_CAP && (
          <div style={{ marginBottom: 8, padding: 8, background: '#fffbe6', border: '1px solid #ffe58f', fontSize: 12 }}>
            Group By is showing the first {GROUP_FETCH_CAP} of {data.total} matching orders. Narrow filters to see all.
          </div>
        )}
        {!grouping ? renderOrdersTable(data?.orders ?? [], false) : renderGroupedLevel(data?.orders ?? [], 0)}
      </div>

      <Modal
        title={`Mark ${selectedRowKeys.length} order${selectedRowKeys.length === 1 ? '' : 's'} as deleted?`}
        open={markDeletedOpen}
        onCancel={() => setMarkDeletedOpen(false)}
        onOk={confirmMarkDeleted}
        okText="Mark Deleted"
        okButtonProps={{
          danger: true,
          disabled: markDeletedReason.trim().length === 0,
          loading: markDeletedBusy,
        }}
        cancelButtonProps={{ disabled: markDeletedBusy }}
        maskClosable={!markDeletedBusy}
      >
        <p style={{ marginTop: 0, marginBottom: 12 }}>
          Use this when ShipStation removed the order upstream (e.g. Sparks IO T110-* deletions). The orders will move out of Awaiting Shipment and can be restored later.
        </p>
        <label style={{ display: 'block', fontWeight: 500, marginBottom: 4 }}>
          Reason <span style={{ color: '#ff4d4f' }}>*</span>
        </label>
        <Input.TextArea
          placeholder="Required — this shows in Order Activity."
          rows={3}
          value={markDeletedReason}
          onChange={(e) => setMarkDeletedReason(e.target.value)}
          disabled={markDeletedBusy}
          autoFocus
        />
      </Modal>

      <Modal
        title={`Ship Selected Orders (${selectedRowKeys.length})`}
        open={shipModalOpen}
        onCancel={() => { setShipModalOpen(false); queryClient.invalidateQueries({ queryKey: ['orders'] }); }}
        width={1300}
        footer={[
          <Button key="cancel" onClick={() => { setShipModalOpen(false); setBatchJobId(null); setBatchStatus(null); queryClient.invalidateQueries({ queryKey: ['orders'] }); }}>Close</Button>,
          <Button key="validate" onClick={handleValidate} loading={validating} disabled={!!batchJobId && batchStatus?.status !== 'complete' && batchStatus?.status !== 'failed'}>Validate</Button>,
          batchJobId && batchStatus?.failed > 0 ? (
            <Button key="retry" onClick={handleRetryBatch}>Retry {batchStatus.failed} Failed</Button>
          ) : null,
          <Button key="confirm" type="primary" disabled={!allValidated || (!!batchJobId && batchStatus?.status !== 'complete' && batchStatus?.status !== 'failed')} loading={confirming || (!!batchJobId && (batchStatus?.status === 'pending' || batchStatus?.status === 'processing'))} onClick={handleShipConfirm}>Confirm Shipment</Button>,
        ].filter(Boolean)}
      >
        {batchJobId && batchStatus && (
          <div style={{ marginBottom: 12, padding: 10, background: '#f0f8ff', border: '1px solid #91d5ff', borderRadius: 4 }}>
            <div style={{ fontWeight: 500 }}>
              Batch {batchStatus.status === 'complete' ? 'complete' : batchStatus.status === 'failed' ? 'failed' : 'in progress'}
            </div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              {batchStatus.succeeded + batchStatus.failed + batchStatus.skipped} of {batchStatus.total} processed — {batchStatus.succeeded} succeeded, {batchStatus.failed} failed, {batchStatus.skipped} skipped
              {(batchStatus.status === 'pending' || batchStatus.status === 'processing') && <span> — safe to close this window, the batch continues in the background</span>}
            </div>
          </div>
        )}
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>Pickup Address:</span>
          <Select
            style={{ flex: 1 }}
            placeholder="Select pickup address"
            value={selectedPickupId}
            onChange={v => { setSelectedPickupId(v); setValidationResults({}); }}
            options={(() => {
              // Only show pickups that are fully configured for UniUni:
              //   - customer_no          → identifies the UniUni account
              //   - inventory_warehouse_id → needed for SS inventory adjust
              //   - uniuni_warehouse_id  → needed for UniUni routing
              // Blank rows from the SS sync stay hidden until admin fills
              // all three from the Pickup Addresses page.
              const nonEmpty = (v: unknown): boolean =>
                v != null && String(v).trim() !== '';
              const validPickups = pickupAddresses.filter(
                (a: any) =>
                  nonEmpty(a.customer_no) &&
                  nonEmpty(a.inventory_warehouse_id) &&
                  nonEmpty(a.uniuni_warehouse_id),
              );
              // Sort: default first, then usage_count DESC, then alpha.
              const prefBy = new Map(pickupPrefs.map((p) => [p.pickup_id, p]));
              validPickups.sort((a: any, b: any) => {
                const pa = prefBy.get(a.id);
                const pb = prefBy.get(b.id);
                if ((pa?.is_default ? 1 : 0) !== (pb?.is_default ? 1 : 0)) {
                  return (pb?.is_default ? 1 : 0) - (pa?.is_default ? 1 : 0);
                }
                if ((pa?.usage_count ?? 0) !== (pb?.usage_count ?? 0)) {
                  return (pb?.usage_count ?? 0) - (pa?.usage_count ?? 0);
                }
                return String(a.name).localeCompare(String(b.name));
              });
              return validPickups.map((a: any) => {
                const pref = prefBy.get(a.id);
                const badges: string[] = [];
                if (pref?.is_default) badges.push('★ Default');
                if ((pref?.usage_count ?? 0) > 0) badges.push(`used ${pref!.usage_count}x`);
                const badgeStr = badges.length ? `  [${badges.join(' · ')}]` : '';
                return {
                  label: `${a.name} — ${[a.address_line1, a.city, a.province, a.postal_code].filter(Boolean).join(', ')}${badgeStr}`,
                  value: a.id,
                };
              });
            })()}
          />
          <Tooltip title={selectedPickupId ? 'Save this pickup as your default for this region' : 'Select a pickup first'}>
            <Button
              size="small"
              disabled={!selectedPickupId || pickupPrefs.find((p) => p.pickup_id === selectedPickupId)?.is_default}
              onClick={handleSetDefaultPickup}
            >
              {pickupPrefs.find((p) => p.pickup_id === selectedPickupId)?.is_default ? '★ Default' : 'Set as default'}
            </Button>
          </Tooltip>
        </div>

        <Table
          size="small"
          pagination={false}
          rowKey="orderId"
          dataSource={selectedOrders}
          scroll={{ x: true }}
          columns={[
            { title: 'Order #', dataIndex: 'orderNumber', width: 120, fixed: 'left' },
            {
              title: 'Delivery Address',
              width: 200,
              render: (_: any, r: any) => {
                const s = r.shipTo ?? {};
                const addr = [s.street1, s.street2, s.city, s.state, s.postalCode, s.country].filter(Boolean).join(', ');
                if (!addr) return <Tag color="red">Missing</Tag>;
                if (!s.street1) return <Tooltip title="Street address missing — re-sync order"><Tag color="orange">{addr}</Tag></Tooltip>;
                return <span>{addr}</span>;
              },
            },
            {
              title: 'Length *',
              width: 100,
              render: (_: any, r: any) => {
                const id = String(r.orderId);
                const err = pkgErrors[id]?.includes('length');
                return (
                  <InputNumber
                    min={0.001}
                    style={{ width: '100%', borderColor: err ? 'red' : undefined }}
                    placeholder="e.g. 30"
                    value={pkgData[id]?.length}
                    onChange={v => setPkg(id, 'length', v)}
                  />
                );
              },
            },
            {
              title: 'Width *',
              width: 100,
              render: (_: any, r: any) => {
                const id = String(r.orderId);
                const err = pkgErrors[id]?.includes('width');
                return (
                  <InputNumber
                    min={0.001}
                    style={{ width: '100%', borderColor: err ? 'red' : undefined }}
                    placeholder="e.g. 20"
                    value={pkgData[id]?.width}
                    onChange={v => setPkg(id, 'width', v)}
                  />
                );
              },
            },
            {
              title: 'Height *',
              width: 100,
              render: (_: any, r: any) => {
                const id = String(r.orderId);
                const err = pkgErrors[id]?.includes('height');
                return (
                  <InputNumber
                    min={0.001}
                    style={{ width: '100%', borderColor: err ? 'red' : undefined }}
                    placeholder="e.g. 10"
                    value={pkgData[id]?.height}
                    onChange={v => setPkg(id, 'height', v)}
                  />
                );
              },
            },
            {
              title: 'Dim UOM *',
              width: 95,
              render: (_: any, r: any) => {
                const id = String(r.orderId);
                const err = pkgErrors[id]?.includes('dimension_uom');
                return (
                  <Select
                    style={{ width: '100%', borderColor: err ? 'red' : undefined }}
                    placeholder="CM/IN"
                    value={pkgData[id]?.dimension_uom}
                    onChange={v => setPkg(id, 'dimension_uom', v)}
                    options={[{ label: 'CM', value: 'CM' }, { label: 'IN', value: 'IN' }]}
                  />
                );
              },
            },
            {
              title: 'Weight *',
              width: 100,
              render: (_: any, r: any) => {
                const id = String(r.orderId);
                const err = pkgErrors[id]?.includes('weight');
                return (
                  <InputNumber
                    min={0.001}
                    style={{ width: '100%', borderColor: err ? 'red' : undefined }}
                    placeholder="e.g. 1.5"
                    value={pkgData[id]?.weight}
                    onChange={v => setPkg(id, 'weight', v)}
                  />
                );
              },
            },
            {
              title: 'Wt UOM *',
              width: 95,
              render: (_: any, r: any) => {
                const id = String(r.orderId);
                const err = pkgErrors[id]?.includes('weight_uom');
                return (
                  <Select
                    style={{ width: '100%', borderColor: err ? 'red' : undefined }}
                    placeholder="KGS…"
                    value={pkgData[id]?.weight_uom}
                    onChange={v => setPkg(id, 'weight_uom', v)}
                    options={[{ label: 'KGS', value: 'KGS' }, { label: 'LBS', value: 'LBS' }, { label: 'OZS', value: 'OZS' }]}
                  />
                );
              },
            },
            {
              title: 'Status',
              width: 80,
              align: 'center' as const,
              render: (_: any, r: any) => {
                const id = String(r.orderId);
                if (validating) return <Spin size="small" />;
                const res = validationResults[id];
                if (!res) return <span style={{ color: '#aaa' }}>—</span>;
                if (res.status === 'SUCCESS') {
                  return (
                    <Tooltip title="Validated successfully">
                      <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 18 }} />
                    </Tooltip>
                  );
                }
                // Long UniUni error strings are annoying inline — pop them into a
                // clickable card. Icon color signals status at a glance.
                // UniUni's error phrasing is awkward ("Cannot validation: ...").
                // Rewrite the common prefixes so ops sees clean copy.
                const raw = (res.ret_msg || res.error || 'Validation failed') as string;
                const reason = raw
                  .replace(/^Cannot validation:\s*/i, '')
                  .replace(/^Cannot create order:\s*/i, '')
                  .trim() || 'Validation failed';
                const popContent = (
                  <div style={{ maxWidth: 360 }}>
                    <Typography.Text strong style={{ color: '#ff4d4f' }}>
                      Validation failed
                    </Typography.Text>
                    <Typography.Paragraph
                      copyable
                      style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap' }}
                    >
                      {reason}
                    </Typography.Paragraph>
                  </div>
                );
                return (
                  <Popover content={popContent} trigger="click" placement="left">
                    <CloseCircleTwoTone
                      twoToneColor="#ff4d4f"
                      style={{ fontSize: 18, cursor: 'pointer' }}
                    />
                  </Popover>
                );
              },
            },
            {
              title: 'Est. Charge',
              width: 100,
              render: (_: any, r: any) => {
                const res = validationResults[String(r.orderId)];
                if (!res?.data) return <span style={{ color: '#aaa' }}>—</span>;
                const charge = res.data.totalAfterTax ?? res.data.shippingCharge ?? 0;
                return <span>${Number(charge).toFixed(2)}</span>;
              },
            },
            {
              title: 'ETA',
              width: 110,
              render: (_: any, r: any) => {
                const res = validationResults[String(r.orderId)];
                if (!res?.data) return <span style={{ color: '#aaa' }}>—</span>;
                return <span>{res.data.eta || '—'}</span>;
              },
            },
          ]}
        />
        {Object.keys(pkgErrors).length > 0 && (
          <div style={{ color: 'red', marginTop: 8 }}>* All dimension/weight fields are required for every order.</div>
        )}
        {(() => {
          if (Object.keys(validationResults).length === 0) return null;
          const failed = selectedOrders.filter((o: any) => {
            const res = validationResults[String(o.orderId)];
            return res && res.status !== 'SUCCESS';
          });
          const passed = selectedOrders.length - failed.length;
          if (failed.length === 0) {
            return (
              <div style={{ marginTop: 8, color: '#52c41a' }}>
                <CheckCircleTwoTone twoToneColor="#52c41a" /> All {passed} order
                {passed === 1 ? '' : 's'} validated successfully.
              </div>
            );
          }
          return (
            <div style={{ marginTop: 8, color: '#ff4d4f' }}>
              <CloseCircleTwoTone twoToneColor="#ff4d4f" />{' '}
              {failed.length} of {selectedOrders.length} order
              {selectedOrders.length === 1 ? '' : 's'} failed validation — click the red
              icon on each row for details.
            </div>
          );
        })()}
      </Modal>

      <Drawer
        title="Filters"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        width={340}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => { clearFilters(); }}>Clear</Button>
            <Button type="primary" onClick={() => { applyFilters(); setFilterDrawerOpen(false); }}>Apply</Button>
          </Space>
        }
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>Store Name</label>
          <Select
            placeholder="Store"
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp="label"
            filterSort={(a, b) => String(a.label).localeCompare(String(b.label))}
            value={local.storeId}
            onChange={val => setLocal({ ...local, storeId: val })}
            options={
              (user?.assignedStores && user.assignedStores.length > 0)
                ? stores
                  .filter((s: any) => user.assignedStores!.map((id: any) => String(id)).includes(String(s.storeId)))
                  .map((s: any) => ({ label: s.storeName, value: s.storeId }))
                : stores.map((s: any) => ({ label: s.storeName, value: s.storeId }))
            }
          />
        </Space>
        {activeTab === 'other' && (
          <Space orientation="vertical" style={{ width: '100%' }}>
            <label>Order Status</label>
            <Select
              placeholder="Status"
              allowClear
              value={local.status}
              style={{ width: '100%' }}
              onChange={val => setLocal({ ...local, status: val })}
              options={[
                { label: 'Awaiting Payment', value: 'awaiting_payment' },
                { label: 'Shipped', value: 'shipped' },
                { label: 'On Hold', value: 'on_hold' },
                { label: 'Cancelled', value: 'cancelled' },
                { label: 'Deleted', value: 'deleted' },
              ]}
            />
          </Space>
        )}

        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>Validation</label>
          <Select
            placeholder="Any"
            allowClear
            value={local.validationStatus}
            style={{ width: '100%' }}
            onChange={val => setLocal({ ...local, validationStatus: val })}
            options={[
              { label: 'Success', value: 'SUCCESS' },
              { label: 'Failed', value: 'FAIL' },
              { label: 'Error', value: 'ERROR' },
              { label: 'Not validated', value: 'UNVALIDATED' },
              { label: 'Any (validated)', value: 'ANY' },
            ]}
          />
        </Space>

        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>Rate ($)</label>
          <Space.Compact style={{ width: '100%' }}>
            <InputNumber
              placeholder="Min"
              min={0}
              style={{ width: '50%' }}
              value={local.rateMin}
              onChange={(v) => setLocal({ ...local, rateMin: v ?? undefined })}
            />
            <InputNumber
              placeholder="Max"
              min={0}
              style={{ width: '50%' }}
              value={local.rateMax}
              onChange={(v) => setLocal({ ...local, rateMax: v ?? undefined })}
            />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>Weight</label>
          <Space.Compact style={{ width: '100%' }}>
            <InputNumber
              placeholder="Min"
              min={0}
              style={{ width: '50%' }}
              value={local.weightMin}
              onChange={(v) => setLocal({ ...local, weightMin: v ?? undefined })}
            />
            <InputNumber
              placeholder="Max"
              min={0}
              style={{ width: '50%' }}
              value={local.weightMax}
              onChange={(v) => setLocal({ ...local, weightMax: v ?? undefined })}
            />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>Order Number</label>
          <Space.Compact style={{ width: '100%' }}>
            <Select value={local.orderNumberOp} onChange={val => setLocal({ ...local, orderNumberOp: val })} options={[{ label: 'Contains', value: 'contains' }, { label: 'Equals', value: 'equals' }, { label: 'Starts With', value: 'startsWith' }, { label: 'Ends With', value: 'endsWith' }]} />
            <Input placeholder="Order Number" value={local.orderNumber} onChange={e => setLocal({ ...local, orderNumber: e.target.value })} />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>Customer Name</label>
          <Space.Compact style={{ width: '100%' }}>
            <Select value={local.nameOp} onChange={val => setLocal({ ...local, nameOp: val })} options={[{ label: 'Contains', value: 'contains' }, { label: 'Equals', value: 'equals' }, { label: 'Starts With', value: 'startsWith' }, { label: 'Ends With', value: 'endsWith' }]} />
            <Input placeholder="Customer Name" value={local.name} onChange={e => setLocal({ ...local, name: e.target.value })} />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>Customer Email</label>
          <Space.Compact style={{ width: '100%' }}>
            <Select value={local.emailOp} onChange={val => setLocal({ ...local, emailOp: val })} options={[{ label: 'Contains', value: 'contains' }, { label: 'Equals', value: 'equals' }, { label: 'Starts With', value: 'startsWith' }, { label: 'Ends With', value: 'endsWith' }]} />
            <Input placeholder="Email" value={local.email} onChange={e => setLocal({ ...local, email: e.target.value })} />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>City</label>
          <Space.Compact style={{ width: '100%' }}>
            <Select value={local.cityOp} onChange={val => setLocal({ ...local, cityOp: val })} options={[{ label: 'Contains', value: 'contains' }, { label: 'Equals', value: 'equals' }, { label: 'Starts With', value: 'startsWith' }, { label: 'Ends With', value: 'endsWith' }]} />
            <Input placeholder="City" value={local.city} onChange={e => setLocal({ ...local, city: e.target.value })} />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>Postal Code</label>
          <Space.Compact style={{ width: '100%' }}>
            <Select value={local.postalCodeOp} onChange={val => setLocal({ ...local, postalCodeOp: val })} options={[{ label: 'Contains', value: 'contains' }, { label: 'Equals', value: 'equals' }, { label: 'Starts With', value: 'startsWith' }, { label: 'Ends With', value: 'endsWith' }]} />
            <Input placeholder="Postal Code" value={local.postalCode} onChange={e => setLocal({ ...local, postalCode: e.target.value })} />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>Country</label>
          <Space.Compact style={{ width: '100%' }}>
            <Select value={local.countryOp} onChange={val => setLocal({ ...local, countryOp: val })} options={[{ label: 'Contains', value: 'contains' }, { label: 'Equals', value: 'equals' }, { label: 'Starts With', value: 'startsWith' }, { label: 'Ends With', value: 'endsWith' }]} />
            <Input placeholder="Country" value={local.country} onChange={e => setLocal({ ...local, country: e.target.value })} />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>Item Name</label>
          <Input placeholder="Item name (partial match)" value={local.itemName} onChange={e => setLocal({ ...local, itemName: e.target.value })} />
        </Space>

        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>Ship From (Warehouse)</label>
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            placeholder="All warehouses (leave empty to see everything)"
            value={local.warehouseIds}
            onChange={(v) => setLocal({ ...local, warehouseIds: v })}
            options={pickupAddresses
              .filter((a: any) => a.ss_warehouse_id != null && a.ss_warehouse_id !== '')
              .map((a: any) => ({ label: `${a.name} (SS #${a.ss_warehouse_id})`, value: String(a.ss_warehouse_id) }))}
          />
        </Space>

        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>Order Date Range</label>
          <DatePicker.RangePicker
            value={local.from && local.to ? [dayjs(local.from), dayjs(local.to)] : undefined}
            onChange={dates =>
              setLocal({
                ...local,
                from: dates?.[0]?.format('YYYY-MM-DD'),
                to: dates?.[1]?.format('YYYY-MM-DD')
              })
            }
          />
        </Space>

      </Drawer>

      <Drawer
        title="Table Columns"
        open={colDrawerOpen}
        onClose={() => setColDrawerOpen(false)}
        width={340}
        footer={
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button onClick={resetPrefs}>Reset to default</Button>
            <Button type="primary" onClick={() => setColDrawerOpen(false)}>Done</Button>
          </Space>
        }
      >
        <div style={{ color: '#666', fontSize: 12, marginBottom: 12 }}>
          Toggle visibility, reorder with the arrows. Preferences save automatically to this browser.
        </div>
        {(() => {
          const byId = new Map(columnCatalog.map((c) => [c.id, c]));
          const ordered: string[] = [
            ...colPrefs.order.filter((id) => byId.has(id)),
            ...defaultOrder.filter((id) => !colPrefs.order.includes(id)),
          ];
          return ordered.map((id, idx) => {
            const meta = byId.get(id)!;
            const isHidden = colPrefs.hidden.includes(id);
            const move = (delta: number) => {
              const next = [...ordered];
              const j = idx + delta;
              if (j < 0 || j >= next.length) return;
              [next[idx], next[j]] = [next[j], next[idx]];
              savePrefs({ order: next, hidden: colPrefs.hidden });
            };
            const toggle = () => {
              const nextHidden = isHidden
                ? colPrefs.hidden.filter((h) => h !== id)
                : [...colPrefs.hidden, id];
              savePrefs({ order: ordered, hidden: nextHidden });
            };
            return (
              <div
                key={id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 8px', borderBottom: '1px solid #f0f0f0',
                }}
              >
                <Checkbox checked={!isHidden} onChange={toggle} />
                <span style={{ flex: 1, color: isHidden ? '#999' : undefined }}>{meta.title}</span>
                <Button
                  type="text" size="small" icon={<ArrowUpOutlined />}
                  disabled={idx === 0} onClick={() => move(-1)}
                />
                <Button
                  type="text" size="small" icon={<ArrowDownOutlined />}
                  disabled={idx === ordered.length - 1} onClick={() => move(1)}
                />
              </div>
            );
          });
        })()}
      </Drawer>

      <Drawer
        title={`Pick Summary — ${selectedRowKeys.length} order${selectedRowKeys.length === 1 ? '' : 's'}`}
        open={invDrawerOpen}
        onClose={() => setInvDrawerOpen(false)}
        width={380}
      >
        {inventorySummary.length === 0 ? (
          <div style={{ color: '#666' }}>Select orders to see aggregated SKUs.</div>
        ) : (
          <>
            <div style={{ marginBottom: 12, fontSize: 12, color: '#666' }}>
              Total <strong>{totalItems}</strong> unit{totalItems === 1 ? '' : 's'} across{' '}
              <strong>{inventorySummary.length}</strong> SKU{inventorySummary.length === 1 ? '' : 's'}.
            </div>
            <Table
              size="small"
              rowKey="sku"
              pagination={false}
              dataSource={inventorySummary}
              columns={[
                { title: 'SKU / Item', dataIndex: 'name', render: (name, r: any) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{name}</div>
                    <div style={{ fontSize: 11, color: '#999' }}>{r.sku}</div>
                  </div>
                )},
                { title: 'Qty', dataIndex: 'qty', width: 60, align: 'right' as const,
                  render: (q: number) => <strong>{q}</strong> },
                { title: 'Orders', dataIndex: 'orderCount', width: 70, align: 'right' as const },
              ]}
            />
          </>
        )}
      </Drawer>

      <Drawer
        title="Shipment Batch Progress"
        open={batchViewerOpen}
        onClose={() => setBatchViewerOpen(false)}
        width={640}
        extra={
          <Space>
            {batchStatus?.failed > 0 && (
              <Button onClick={handleRetryBatch}>Retry {batchStatus.failed} Failed</Button>
            )}
            {batchJobId && (batchStatus?.status === 'complete' || batchStatus?.status === 'failed') && (
              <Button onClick={() => { setBatchJobId(null); setBatchStatus(null); setBatchViewerOpen(false); }}>
                Dismiss
              </Button>
            )}
          </Space>
        }
      >
        {batchStatus && (
          <>
            <div style={{ marginBottom: 12, padding: 10, background: '#fafafa', border: '1px solid #eee', borderRadius: 4 }}>
              <div><strong>Status:</strong> {batchStatus.status}</div>
              <div style={{ marginTop: 4, fontSize: 13 }}>
                {batchStatus.succeeded + batchStatus.failed + batchStatus.skipped} of {batchStatus.total} processed
                — {batchStatus.succeeded} ok, {batchStatus.failed} failed, {batchStatus.skipped} skipped
              </div>
              {batchStatus.error_message && (
                <div style={{ marginTop: 6, color: '#cf1322', fontSize: 12 }}>{batchStatus.error_message}</div>
              )}
              {(batchStatus.status === 'pending' || batchStatus.status === 'processing') && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
                  Runs in the background — you can close this and come back anytime.
                </div>
              )}
            </div>
            <Table
              size="small"
              pagination={false}
              rowKey={(r) => (r as any).orderId}
              dataSource={Object.entries(batchStatus.per_order_state ?? {}).map(([orderId, s]: [string, any]) => ({ orderId, ...s }))}
              columns={[
                { title: 'Order ID', dataIndex: 'orderId', width: 120 },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  width: 100,
                  render: (s: string) =>
                    s === 'success' ? <Tag color="green">Success</Tag>
                    : s === 'error' ? <Tag color="red">Failed</Tag>
                    : s === 'skipped' ? <Tag color="orange">Skipped</Tag>
                    : <Tag>Pending</Tag>,
                },
                { title: 'Tracking', dataIndex: 'trackingNumber', render: (v: string) => v ?? '-' },
                { title: 'Error', dataIndex: 'error', render: (v: string) => v ? <span style={{ color: '#cf1322' }}>{v}</span> : '-' },
              ]}
            />
          </>
        )}
      </Drawer>

      <Modal
        title={stockDetail ? `Stock breakdown · Order ${stockDetail.orderNumber ?? stockDetail.orderId}` : 'Stock breakdown'}
        open={!!stockDetail}
        onCancel={() => setStockDetail(null)}
        footer={<Button onClick={() => setStockDetail(null)}>Close</Button>}
        width={720}
      >
        {stockDetail && (() => {
          const parseItems = (v: any): any[] => (Array.isArray(v) ? v : typeof v === 'string' ? (() => { try { return JSON.parse(v); } catch { return []; } })() : []);
          const list: any[] = parseItems(stockDetail.items);
          const storeId = Number(stockDetail.storeId ?? 0);
          const rows: any[] = [];
          for (const it of list) {
            const sku = (it.sku ?? '').toString().trim();
            const qty = Number(it.quantity ?? 1);
            const master = masterFor[`${sku}::${storeId}`] ?? sku;
            const whList = stockBySku[master] ?? [];
            if (whList.length === 0) {
              rows.push({ key: `${sku}-none`, sku, master, qty, warehouse_name: '-', warehouse_id: '-', location_name: '-', location_id: '-', available: null, on_hand: null, snapshot_at: null });
            } else {
              for (const wh of whList) {
                rows.push({
                  key: `${sku}-${wh.warehouse_id}-${wh.location_id ?? 'nolocation'}`,
                  sku, master, qty,
                  warehouse_name: wh.warehouse_name ?? wh.warehouse_id,
                  warehouse_id: wh.warehouse_id,
                  location_name: wh.location_name ?? '(Unspecified)',
                  location_id: wh.location_id ?? '-',
                  available: wh.available,
                  on_hand: wh.on_hand,
                  snapshot_at: wh.snapshot_at,
                });
              }
            }
          }
          // Unique warehouses across all rows for the selector.
          const whOptions = Array.from(
            new Map(
              rows
                .filter((r) => r.warehouse_id && r.warehouse_id !== '-')
                .map((r) => [r.warehouse_id, { key: r.warehouse_id, label: `${r.warehouse_name} (${r.warehouse_id})` }])
            ).values()
          );
          const filteredRows = stockDetailLocation === 'ALL'
            ? rows
            : rows.filter((r) => r.warehouse_id === stockDetailLocation);

          // Per-item fulfillment check for the selected warehouse.
          const perItemCheck: Array<{ sku: string; qty: number; avail: number | null }> = [];
          if (stockDetailLocation !== 'ALL') {
            for (const it of list) {
              const sku = (it.sku ?? '').toString().trim();
              const qty = Number(it.quantity ?? 1);
              const match = rows.find(
                (r) => r.sku === sku && r.warehouse_id === stockDetailLocation,
              );
              perItemCheck.push({ sku, qty, avail: match?.available ?? match?.on_hand ?? null });
            }
          }
          const canFulfill = perItemCheck.length > 0 && perItemCheck.every((p) => p.avail != null && p.avail >= p.qty);

          return (
            <>
              <Space style={{ marginBottom: 12 }} align="center">
                <span>Filter by warehouse:</span>
                <Select
                  style={{ minWidth: 260 }}
                  value={stockDetailLocation}
                  onChange={setStockDetailLocation}
                  options={[{ value: 'ALL', label: 'All warehouses' }, ...whOptions.map((l) => ({ value: l.key, label: l.label }))]}
                />
                {stockDetailLocation !== 'ALL' && (
                  <Tag color={canFulfill ? 'green' : 'red'}>
                    {canFulfill ? 'Can fulfill' : 'Insufficient stock'}
                  </Tag>
                )}
              </Space>

              {stockDetailLocation !== 'ALL' && perItemCheck.length > 0 && (
                <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                  {perItemCheck.map((p) => (
                    <span key={p.sku} style={{ marginRight: 12 }}>
                      <strong>{p.sku}</strong>: {p.avail ?? '?'} / need {p.qty}
                      {p.avail != null && p.avail < p.qty && <span style={{ color: '#cf1322' }}> (short {p.qty - p.avail})</span>}
                    </span>
                  ))}
                </div>
              )}

              <Table
                size="small"
                pagination={false}
                dataSource={filteredRows}
                columns={[
                  { title: 'Ordered SKU', dataIndex: 'sku' },
                  { title: 'Master SKU', dataIndex: 'master' },
                  { title: 'Qty', dataIndex: 'qty', width: 60, align: 'right' as const },
                  { title: 'Warehouse', dataIndex: 'warehouse_name',
                    render: (v: string, r: any) => `${v}${r.warehouse_id !== '-' ? ` (${r.warehouse_id})` : ''}` },
                  { title: 'Location', dataIndex: 'location_name',
                    render: (v: string, r: any) => `${v}${r.location_id && r.location_id !== '-' ? ` (${r.location_id})` : ''}` },
                  { title: 'On Hand', dataIndex: 'on_hand', width: 90, align: 'right' as const,
                    render: (v: number | null) => (v == null ? '-' : v) },
                  { title: 'Available', dataIndex: 'available', width: 90, align: 'right' as const,
                    render: (v: number | null, r: any) => {
                      if (v == null) return '-';
                      const color = v <= 0 || v < r.qty ? '#cf1322' : v < r.qty * 2 ? '#d46b08' : '#389e0d';
                      return <strong style={{ color }}>{v}</strong>;
                    }
                  },
                  { title: 'Snapshot At', dataIndex: 'snapshot_at', width: 170,
                    render: (v: string | null) => (v ? new Date(v).toLocaleString() : '-') },
                ]}
              />
            </>
          );
        })()}
      </Modal>
    </div>
  );
}
